import React, { useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useParams } from "react-router-dom";
import DashboardContent from "../../components/DashboardContent";
import useModalWindow from "../../hooks/useModalWindow";
import useCsr from "../../hooks/useCsr";
import { CsrAPI } from "../../apis/CsrAPI";
import ProgressBar from "../../utils/ProgressBar";
import Loader from "../../utils/Loader";
import { FILE_UPLOAD_STATUS, FILES_STATUS } from "../../utils/Constants";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import { ToastContainer } from "react-toastify";
import { ReactComponent as DeleteFile } from "../../assets/images/icons/delete-small.svg";
import { ReactComponent as FileUploaded } from "../../assets/images/icons/file-uploaded.svg";
import { ReactComponent as FileProcessing } from "../../assets/images/icons/file-processing.svg";
import { ReactComponent as FileError } from "../../assets/images/icons/file-error.svg";
import { ReactComponent as FileProcessed } from "../../assets/images/icons/file-processed.svg";
import { ReactComponent as BinBlack } from "../../assets/images/icons/bin-black.svg";
import { ReactComponent as RefreshBlack } from "../../assets/images/icons/refresh-black.svg";
import UploadStatusBar from "../../components/UploadStatusBar";
import { Tooltip } from "react-tooltip";
import { useAuth0 } from "@auth0/auth0-react";
import { isSandboxDemoAccount } from "../../utils/EnvironmentUtils";
import { usePermissions } from "../../contexts/PermissionsContext";
import { NO_PERMISSION_TITLE, PERMISSIONS } from "../../utils/permissions";

let documentType = "";
let changeFileHelper = {};

const ALLOWED_FILE_TYPES = {
  CSP: ["DOCX"],
  SAP: ["DOCX"],
  TFL: ["DOCX", "RTF"],
};

const CSRFileUpload = () => {
  const { studyId } = useParams();
  const { modalWindow, handleCancel } = useModalWindow();
  const {
    setAjaxLoading,
    errMsg,
    setErrMsg,
    errRef,
    setFilesStatus,
    getDocuments,
    cspProgressRef,
    sapProgressRef,
    tflProgressRef,
    cspProgress,
    setCspProgress,
    sapProgress,
    setSapProgress,
    tflProgress,
    setTflProgress,
    documentsList,
  } = useCsr();
  const { user } = useAuth0();
  // Sandbox-only: block the upload area for the demo accounts.
  const showDemoOverlay = isSandboxDemoAccount(user?.email);
  const { hasPermission } = usePermissions();
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  // csr:files:create covers adding and replacing files, csr:files:delete the
  // per-file bin and "Delete all Tables and Figures", csr:files:update the
  // refresh. Read-only users still see the list the study already holds.
  const canUploadFiles = hasPermission(PERMISSIONS.csrFilesCreate);
  const canDeleteFiles = hasPermission(PERMISSIONS.csrFilesDelete);
  const canRefreshFiles = hasPermission(PERMISSIONS.csrFilesUpdate);

  useEffect(() => {
    const allDocs = [
      ...(documentsList?.csp || []),
      ...(documentsList?.sap || []),
      ...(documentsList?.tfl || []),
    ].map(doc => ({
      filename: doc.filename,
      status: doc.status,
      document_type: doc.document_type,
      document_id: doc.document_id,
    }));

    setUploadedDocuments(prev => {
      // Start with API documents as the source of truth
      const updatedDocs = [...allDocs];

      // Add any local "uploading" documents that aren't yet in the API response
      // This handles the case where files are just being uploaded
      prev.forEach(localDoc => {
        if (localDoc.status === FILE_UPLOAD_STATUS.uploading) {
          const existsInApi = allDocs.some(
            apiDoc =>
              apiDoc.filename.split(".")[0] ===
                localDoc.filename.split(".")[0] &&
              apiDoc.document_type === localDoc.document_type
          );

          if (!existsInApi) {
            updatedDocs.push(localDoc);
          }
        }
      });

      return updatedDocs;
    });
  }, [documentsList]);

  function isIterable(obj) {
    // checks for null and undefined
    if (obj == null) {
      return false;
    }
    return typeof obj[Symbol.iterator] === "function";
  }

  const upload = (idx, file, docType) => {
    setFilesStatus(FILES_STATUS.progress);
    /* eslint-disable no-unsafe-optional-chaining */
    let _cspProgress = isIterable(cspProgressRef?.current?.val)
      ? [...cspProgressRef?.current?.val]
      : [];
    let _sapProgress = isIterable(sapProgressRef?.current?.val)
      ? [...sapProgressRef?.current?.val]
      : [];
    let _tflProgress = isIterable(tflProgressRef?.current?.val)
      ? [...tflProgressRef?.current?.val]
      : [];
    /* eslint-enable no-unsafe-optional-chaining */
    return CsrAPI.uploadDocument({ studyId, file, docType }, event => {
      if (docType === "CSP") {
        _cspProgress[idx].percentage = Math.round(
          (100 * event.loaded) / event.total
        );
        setCspProgress({ val: _cspProgress });
      } else if (docType === "SAP") {
        _sapProgress[idx].percentage = Math.round(
          (100 * event.loaded) / event.total
        );
        setSapProgress({ val: _sapProgress });
      } else if (docType === "TFL") {
        _tflProgress[idx].percentage = Math.round(
          (100 * event.loaded) / event.total
        );
        setTflProgress({ val: _tflProgress });
      }
    })
      .then(() => {
        if (docType === "CSP") {
          _cspProgress[idx].percentage = -1;
          setCspProgress({ val: _cspProgress });
        } else if (docType === "SAP") {
          _sapProgress[idx].percentage = -1;
          setSapProgress({ val: _sapProgress });
        } else if (docType === "TFL") {
          _tflProgress[idx].percentage = -1;
          setTflProgress({ val: _tflProgress });
        }

        getDocuments(studyId);
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (docType === "CSP") {
          _cspProgress[idx].percentage = 0;
          setCspProgress({ val: _cspProgress });
        } else if (docType === "SAP") {
          _sapProgress[idx].percentage = 0;
          setSapProgress({ val: _sapProgress });
        } else if (docType === "TFL") {
          _tflProgress[idx].percentage = 0;
          setTflProgress({ val: _tflProgress });
        }

        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Fail to upload document");
        }
        errRef.current?.focus();
      });
  };

  const handleChange = file => {
    //delete previous file if needed
    if (Object.keys(changeFileHelper).length > 0) {
      CsrAPI.deleteDocument({
        studyId: changeFileHelper.studyId,
        documentId: changeFileHelper.documentId,
      })
        .then(() => {
          getDocuments(studyId);
          changeFileHelper = {};
        })
        .catch(error => {
          if (!error?.response) {
            setErrMsg("No Server Response");
          } else if (error.response?.status === 401) {
            setErrMsg("Invalid user credentials");
          } else {
            setErrMsg("Fail to upload document");
          }
          errRef.current?.focus();
        });
    }

    //add files
    let files = [];
    if (file instanceof File) {
      files[0] = file;
    } else {
      files = Array.from(file);
    }

    const newUploadedDocs = files.map(file => ({
      document_type: documentType,
      filename: file.name,
      status: FILE_UPLOAD_STATUS.uploading,
      document_id: null,
    }));

    setUploadedDocuments(prev => [...prev, ...newUploadedDocs]);

    let _progressInfos = files.map(file => ({
      percentage: 0,
      fileName: file.name,
    }));

    if (documentType === "CSP") {
      cspProgressRef.current = {
        val: _progressInfos,
      };
    } else if (documentType === "SAP") {
      sapProgressRef.current = {
        val: _progressInfos,
      };
    } else if (documentType === "TFL") {
      tflProgressRef.current = {
        val: _progressInfos,
      };
    }

    const uploadPromises = files.map((file, i) =>
      upload(i, file, documentType)
    );

    Promise.all(uploadPromises)
      .then(() => {
        getDocuments(studyId);
      })
      .then(() => {
        setCspProgress({ val: [] });
        setSapProgress({ val: [] });
        setTflProgress({ val: [] });
      });

    handleCancel();
  };

  const handleUploadPopup = fileTypeName => {
    if (!canUploadFiles) return;

    documentType = fileTypeName;
    const fileTypes = ALLOWED_FILE_TYPES[documentType];

    if (fileTypeName === "TFL") fileTypeName = "Tables and Figures";
    else fileTypeName = fileTypeName + " file";

    modalWindow({
      title: "Add " + fileTypeName,
      message: (
        <FileUploader
          handleChange={handleChange}
          name="file"
          types={fileTypes}
          label={"Upload or Drop your files here to upload"}
          classes={"drop_area"}
          multiple={documentType === "TFL"}
        />
      ),
      confirmButtonLabel: "",
    });
  };

  const handleFileChange = (studyId, documentId, fileTypeName) => {
    if (!canUploadFiles) return;

    changeFileHelper = { studyId: studyId, documentId: documentId };
    handleUploadPopup(fileTypeName);
  };

  const handleFileDelete = (studyId, documentId) => {
    if (!canDeleteFiles) return;

    setAjaxLoading(true);

    setUploadedDocuments(prev =>
      prev.filter(doc => doc.document_id !== documentId)
    );

    CsrAPI.deleteDocument({ studyId, documentId })
      .then(() => {
        getDocuments(studyId);
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Fail to upload document");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });

    handleCancel();
  };

  const handleDeleteAllTfls = async () => {
    if (!canDeleteFiles) return;

    const confirmed = await modalWindow({
      title: "Delete All Tables and Figures",
      message:
        "Do you want to delete all previously uploaded Tables and Figures?",
      confirmButtonLabel: "Yes",
    });

    if (!confirmed) return;

    setAjaxLoading(true);
    CsrAPI.deleteAllTfls({ studyId })
      .then(() => {
        setTflProgress({ val: [] });
        getDocuments(studyId);
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Failed to delete all Tables and Figures");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  };

  const handleReloadTfls = async () => {
    if (!canRefreshFiles) return;

    const confirmed = await modalWindow({
      title: "Warning",
      message:
        "Performing a refresh may impact selected wizard answers and modify associated data mappings. Please ensure you review all entries after refreshing.",
      confirmButtonLabel: "OK",
    });

    if (!confirmed) return;

    setAjaxLoading(true);
    CsrAPI.reloadTfls({ studyId })
      .then(() => {
        getDocuments(studyId);
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Failed to reload Tables and Figures");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  };

  const PageContent = () => {
    const CspProgressCode = () => {
      if (
        documentsList?.csp?.length > 0 ||
        (cspProgress && cspProgress?.val?.length > 0)
      ) {
        return (
          <div>
            <label className="file-upload-label">Add CSP file</label>
            <div className={"csr-file-upload__component-uploaded"}>
              <div className={"csr-file-upload__component-uploaded__left"}>
                {documentsList?.csp?.map(doc => (
                  <div
                    key={doc.document_id}
                    className={"csr-file-upload__component-uploaded__doc"}>
                    {doc.status === FILE_UPLOAD_STATUS.processing ? (
                      <FileProcessing />
                    ) : doc.status === FILE_UPLOAD_STATUS.processed ? (
                      <FileProcessed />
                    ) : doc.status === FILE_UPLOAD_STATUS.errored ? (
                      <FileError />
                    ) : (
                      <FileUploaded />
                    )}

                    <label>{doc.filename}</label>
                    <button
                      className={"delete"}
                      disabled={!canDeleteFiles}
                      title={canDeleteFiles ? undefined : NO_PERMISSION_TITLE}
                      onClick={() =>
                        handleFileDelete(doc.study_id, doc.document_id)
                      }>
                      <DeleteFile />
                    </button>
                  </div>
                ))}

                {cspProgress &&
                  cspProgress?.val?.length > 0 &&
                  cspProgress?.val?.map((info, index) => (
                    <div
                      key={"csp-" + index}
                      className={`csr-file-upload__component-uploading${info.percentage === -1 ? " hide" : ""}`}>
                      <label>{info.fileName}</label>
                      <div className={"progress-bar__wrapper"}>
                        <Loader loading={true} />
                        <ProgressBar completed={info.percentage} />
                      </div>
                    </div>
                  ))}
              </div>
              <div className={"csr-file-upload__component-uploaded__right"}>
                <button
                  className={
                    "input-submit input-submit__tertiary input-submit__small"
                  }
                  disabled={!canUploadFiles}
                  title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
                  onClick={() =>
                    handleFileChange(
                      documentsList.csp[0].study_id,
                      documentsList.csp[0].document_id,
                      "CSP"
                    )
                  }>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: "8px" }}>
                    <path
                      d="M8 10L8 2M8 2L5 5M8 2L11 5M2 12L14 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Change CSP file
                </button>
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div className={"csr-file-upload__component"}>
            <label>Add CSP file</label>
            <button
              className={
                "input-submit input-submit__primary input-submit__small"
              }
              disabled={!canUploadFiles}
              title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
              onClick={() => handleUploadPopup("CSP")}>
              Add CSP file
            </button>
          </div>
        );
      }
    };

    const SapProgressCode = () => {
      if (
        documentsList?.sap?.length > 0 ||
        (sapProgress && sapProgress?.val?.length > 0)
      ) {
        return (
          <div>
            <label className="file-upload-label">Add SAP file</label>
            <div className={"csr-file-upload__component-uploaded"}>
              <div className={"csr-file-upload__component-uploaded__left"}>
                {documentsList?.sap?.map(doc => (
                  <div
                    key={doc.document_id}
                    className={"csr-file-upload__component-uploaded__doc"}>
                    {doc.status === FILE_UPLOAD_STATUS.processing ? (
                      <FileProcessing />
                    ) : doc.status === FILE_UPLOAD_STATUS.processed ? (
                      <FileProcessed />
                    ) : doc.status === FILE_UPLOAD_STATUS.errored ? (
                      <FileError />
                    ) : (
                      <FileUploaded />
                    )}

                    <label>{doc.filename}</label>
                    <button
                      className={"delete"}
                      disabled={!canDeleteFiles}
                      title={canDeleteFiles ? undefined : NO_PERMISSION_TITLE}
                      onClick={() =>
                        handleFileDelete(doc.study_id, doc.document_id)
                      }>
                      <DeleteFile />
                    </button>
                  </div>
                ))}

                {sapProgress &&
                  sapProgress?.val?.length > 0 &&
                  sapProgress?.val?.map((info, index) => (
                    <div
                      key={"sap-" + index}
                      className={`csr-file-upload__component-uploading${info.percentage === -1 ? " hide" : ""}`}>
                      <label>{info.fileName}</label>
                      <div className={"progress-bar__wrapper"}>
                        <Loader loading={true} />
                        <ProgressBar completed={info.percentage} />
                      </div>
                    </div>
                  ))}
              </div>
              <div className={"csr-file-upload__component-uploaded__right"}>
                <button
                  className={
                    "input-submit input-submit__tertiary input-submit__small"
                  }
                  disabled={!canUploadFiles}
                  title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
                  onClick={() =>
                    handleFileChange(
                      documentsList.sap[0].study_id,
                      documentsList.sap[0].document_id,
                      "SAP"
                    )
                  }>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: "8px" }}>
                    <path
                      d="M8 10L8 2M8 2L5 5M8 2L11 5M2 12L14 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>{" "}
                  Change SAP file
                </button>
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div className={"csr-file-upload__component"}>
            <label>Add SAP file</label>
            <button
              className={
                "input-submit input-submit__primary input-submit__small"
              }
              disabled={!canUploadFiles}
              title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
              onClick={() => handleUploadPopup("SAP")}>
              Add SAP file
            </button>
          </div>
        );
      }
    };

    const TflProgressCode = () => {
      if (
        documentsList?.tfl?.length > 0 ||
        (tflProgress && tflProgress?.val?.length > 0)
      ) {
        return (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
              <label className="file-upload-label" style={{ marginBottom: 0 }}>
                Add Tables and Figures
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                {(() => {
                  const isTflUploading = uploadedDocuments.some(
                    doc =>
                      doc.document_type === "TFL" &&
                      doc.status === FILE_UPLOAD_STATUS.uploading
                  );
                  const deleteAllDisabled = isTflUploading || !canDeleteFiles;
                  const refreshAllDisabled = isTflUploading || !canRefreshFiles;
                  return (
                    <>
                      <button
                        style={{
                          background: "none",
                          border: "none",
                          cursor: deleteAllDisabled ? "not-allowed" : "pointer",
                          padding: 0,
                          opacity: deleteAllDisabled ? 0.5 : 1,
                        }}
                        disabled={deleteAllDisabled}
                        title={canDeleteFiles ? undefined : NO_PERMISSION_TITLE}
                        data-tooltip-id="delete-all-tfls"
                        onClick={handleDeleteAllTfls}>
                        <BinBlack />
                      </button>
                      <Tooltip
                        id="delete-all-tfls"
                        place="top"
                        delayShow={100}
                        className="custom-tooltip">
                        Delete all Tables and Figures
                      </Tooltip>
                      <button
                        style={{
                          background: "none",
                          border: "none",
                          cursor: refreshAllDisabled
                            ? "not-allowed"
                            : "pointer",
                          padding: 0,
                          opacity: refreshAllDisabled ? 0.5 : 1,
                        }}
                        disabled={refreshAllDisabled}
                        title={
                          canRefreshFiles ? undefined : NO_PERMISSION_TITLE
                        }
                        data-tooltip-id="refresh-all-tfls"
                        onClick={handleReloadTfls}>
                        <RefreshBlack />
                      </button>
                      <Tooltip
                        id="refresh-all-tfls"
                        place="top"
                        delayShow={100}
                        className="custom-tooltip">
                        Refresh all Tables and Figures
                      </Tooltip>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className={"csr-file-upload__component-uploaded"}>
              <div className={"csr-file-upload__component-uploaded__left"}>
                {documentsList?.tfl?.map(doc => (
                  <div
                    key={doc.document_id}
                    className={"csr-file-upload__component-uploaded__doc"}>
                    {doc.status === FILE_UPLOAD_STATUS.processing ? (
                      <FileProcessing />
                    ) : doc.status === FILE_UPLOAD_STATUS.processed ? (
                      <FileProcessed />
                    ) : doc.status === FILE_UPLOAD_STATUS.errored ? (
                      <FileError />
                    ) : (
                      <FileUploaded />
                    )}

                    <label>{doc.filename}</label>
                    <button
                      className={"delete"}
                      disabled={!canDeleteFiles}
                      title={canDeleteFiles ? undefined : NO_PERMISSION_TITLE}
                      onClick={() =>
                        handleFileDelete(doc.study_id, doc.document_id)
                      }>
                      <DeleteFile />
                    </button>
                  </div>
                ))}

                {tflProgress &&
                  tflProgress?.val?.length > 0 &&
                  tflProgress?.val?.map((info, index) => (
                    <div
                      key={"sap-" + index}
                      className={`csr-file-upload__component-uploading${info.percentage === -1 ? " hide" : ""}`}>
                      <label>{info.fileName}</label>
                      <div className={"progress-bar__wrapper"}>
                        <Loader loading={true} />
                        <ProgressBar completed={info.percentage} />
                      </div>
                    </div>
                  ))}
              </div>
              <div className={"csr-file-upload__component-uploaded__right"}>
                <button
                  className={
                    "input-submit input-submit__tertiary input-submit__small"
                  }
                  disabled={!canUploadFiles}
                  title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
                  onClick={() => handleUploadPopup("TFL")}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: "8px" }}>
                    <path
                      d="M8 10L8 2M8 2L5 5M8 2L11 5M2 12L14 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>{" "}
                  Add additional Tables and Figures
                </button>
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div className={"csr-file-upload__component"}>
            <label>Add Tables and Figures</label>
            <button
              className={
                "input-submit input-submit__primary input-submit__small"
              }
              disabled={!canUploadFiles}
              title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
              onClick={() => handleUploadPopup("TFL")}>
              Add Tables and Figures
            </button>
          </div>
        );
      }
    };

    return (
      <div className={"csr-file-upload csr-file-upload__wrapper"}>
        <UploadStatusBar documents={uploadedDocuments} />

        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>

        {CspProgressCode()}
        {SapProgressCode()}
        {TflProgressCode()}
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Upload your CSP, SAP and Tables and Figures"}
      headerButton={false}
      pageContent={PageContent()}
      contentOverlay={showDemoOverlay}
      prevButton={{
        buttonTitle: "Study Name",
        buttonGoToAction: "/csr/" + studyId + "/summary",
      }}
      // nextButton={{
      //   buttonTitle: "Wizard Questions",
      //   buttonGoToAction: "/csr/" + studyId + "/questions",
      // }}

      nextButton={{
        buttonTitle: "Study Design & Demographics",
        buttonGoToAction: "/csr/" + studyId + "/study-design-demographics",
      }}
    />
  );
};

export default CSRFileUpload;
